import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, tokenManager } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getRoleCodes = (u) => {
    if (!u) return [];
    if (Array.isArray(u.roles_info)) {
      return u.roles_info.map((r) => r.code || r.name).filter(Boolean);
    }
    if (Array.isArray(u.roles)) {
      return u.roles.map((r) => (typeof r === 'string' ? r : r.code || r.name)).filter(Boolean);
    }
    return [];
  };

  // 初始化检查登录状态
  useEffect(() => {
    const initAuth = async () => {
      const token = tokenManager.getAccessToken();
      const storedUser = tokenManager.getUser();

      if (token && storedUser) {
        setUser(storedUser);
        // 后台验证 token 有效性
        try {
          const response = await authAPI.getProfile();
          setUser(response.data);
          tokenManager.setUser(response.data);
        } catch (err) {
          // Token 无效，清除登录状态
          tokenManager.clearTokens();
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (username, password) => {
    setError(null);
    try {
      const response = await authAPI.login(username, password);
      const { access, refresh, user: userData } = response.data;

      tokenManager.setTokens(access, refresh);
      tokenManager.setUser(userData);
      setUser(userData);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail ||
        err.response?.data?.non_field_errors?.[0] ||
        '登录失败，请检查用户名和密码';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      // 即使登出请求失败，也清除本地状态
      console.error('Logout error:', err);
    } finally {
      tokenManager.clearTokens();
      setUser(null);
    }
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(userData);
    tokenManager.setUser(userData);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await authAPI.getProfile();
      setUser(response.data);
      tokenManager.setUser(response.data);
      return response.data;
    } catch (err) {
      console.error('Refresh user error:', err);
      throw err;
    }
  }, []);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isSuperuser: !!(user?.is_superuser || getRoleCodes(user).includes('super_admin')),
    isAdmin: !!(user?.is_staff || user?.is_superuser || getRoleCodes(user).includes('admin') || getRoleCodes(user).includes('super_admin')),
    isDepartmentManager: !!getRoleCodes(user).includes('department_manager'),
    login,
    logout,
    updateUser,
    refreshUser,
    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

